import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import ModalCloseBtn from "@/components/ModalCloseBtn";
import { Image as ImageIcon, Plus, Trash2, ExternalLink, Link as LinkIcon, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { getNav } from "@/pages/navConfigs";

export default function DesignGalleryPage() {
  const { user, customRole } = useAuth();
  const [designs, setDesigns] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/designs");
      setDesigns(r.data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (d) => {
    if (!window.confirm(`Remove "${d.name}" from your gallery?`)) return;
    await api.delete(`/designs/${d.design_id}`);
    load();
  };

  return (
    <Layout navItems={getNav(user.role, customRole)}>
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="design-gallery-page">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center text-white"><ImageIcon className="w-6 h-6" /></div>
            <div>
              <h1 className="font-display font-black text-2xl text-slate-900">Design Gallery</h1>
              <p className="text-sm text-slate-500">Store links to every design you make. Thumbnails auto-load from Drive/Canva/OneDrive/any URL.</p>
            </div>
          </div>
          <button onClick={() => setShowAdd(true)} className="btn-primary" data-testid="design-add-btn"><Plus className="w-4 h-4" /> Add design</button>
        </div>

        {loading ? (
          <div className="dashboard-card p-12 text-center text-slate-400">Loading…</div>
        ) : designs.length === 0 ? (
          <div className="dashboard-card p-12 text-center">
            <ImageIcon className="w-10 h-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-semibold">Your gallery is empty</p>
            <p className="text-xs text-slate-400 mt-1">Click "Add design" to save your first link — we'll try to fetch its thumbnail automatically.</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {designs.map((d) => (
              <div key={d.design_id} className="dashboard-card overflow-hidden group" data-testid={`design-card-${d.design_id}`}>
                <a href={d.link} target="_blank" rel="noreferrer" className="block relative aspect-square bg-gradient-to-br from-slate-100 to-slate-50 overflow-hidden">
                  {d.thumbnail_url ? (
                    <img src={d.thumbnail_url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <div className="flex items-center justify-center h-full text-slate-300"><ImageIcon className="w-10 h-10" /></div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-3">
                    <ExternalLink className="w-4 h-4 text-white" />
                  </div>
                </a>
                <div className="p-3">
                  <div className="font-bold text-sm text-slate-900 truncate" title={d.name}>{d.name}</div>
                  {d.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {d.tags.slice(0, 3).map((t) => <span key={t} className="text-[9px] px-1.5 py-0.5 rounded-full bg-fuchsia-50 text-fuchsia-700">{t}</span>)}
                    </div>
                  )}
                  <div className="flex items-center justify-between mt-2 text-[10px] text-slate-400">
                    <a href={d.link} target="_blank" rel="noreferrer" className="hover:underline flex items-center gap-1 truncate max-w-[75%]"><LinkIcon className="w-2.5 h-2.5" /> Open</a>
                    <button onClick={() => del(d)} className="text-rose-500 hover:bg-rose-50 p-1 rounded" data-testid={`design-del-${d.design_id}`}><Trash2 className="w-3 h-3" /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {showAdd && <AddDesignModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); load(); }} />}
      </div>
    </Layout>
  );
}

function AddDesignModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", link: "", thumbnail_url: "", tags: "" });
  const [busy, setBusy] = useState(false);
  const [thumbBusy, setThumbBusy] = useState(false);

  const detectThumb = async () => {
    if (!form.link) return;
    setThumbBusy(true);
    try {
      const r = await api.get("/tools/thumbnail", { params: { url: form.link } });
      if (r.data?.url) {
        setForm((f) => ({ ...f, thumbnail_url: r.data.url }));
        toast.success("Thumbnail found");
      } else {
        toast.info("Could not auto-fetch — paste a thumbnail URL manually.");
      }
    } catch { toast.error("Detection failed"); }
    finally { setThumbBusy(false); }
  };

  const save = async () => {
    if (!form.link.trim()) return toast.error("Link required");
    if (!form.name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        link: form.link.trim(),
        thumbnail_url: form.thumbnail_url.trim(),
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      };
      await api.post("/designs", payload);
      toast.success("Design added");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full relative" data-testid="design-add-modal">
        <ModalCloseBtn onClose={onClose} />
        <h3 className="font-display font-black text-xl mb-4">Add design to gallery</h3>
        <div className="space-y-3">
          <input placeholder="Design name" className="input-pill" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="design-name-input" />
          <div className="flex gap-2">
            <input placeholder="Paste link (Drive, Canva, OneDrive, anything)" className="input-pill flex-1" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="design-link-input" />
            <button onClick={detectThumb} disabled={!form.link || thumbBusy} className="btn-ghost text-xs disabled:opacity-50">{thumbBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Detect</button>
          </div>
          <input placeholder="Thumbnail URL (optional — will auto-fetch on save)" className="input-pill" value={form.thumbnail_url} onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })} />
          {form.thumbnail_url && (
            <img src={form.thumbnail_url} alt="preview" className="w-full max-h-40 object-cover rounded-lg border border-slate-100" onError={(e) => { e.target.style.display = "none"; }} />
          )}
          <input placeholder="Tags (comma-separated: mun, poster, invite)" className="input-pill" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={save} disabled={busy} className="btn-primary flex-1 py-2.5 disabled:opacity-50" data-testid="design-save-btn">{busy ? "Saving…" : "Save"}</button>
          <button onClick={onClose} className="btn-ghost px-4 py-2.5">Cancel</button>
        </div>
      </div>
    </div>
  );
}
